<template>
  <div class="app-wrapper h-full w-full flex flex-col bg-f5f5f5">
    <!-- 顶部导航栏 -->
    <AppHeader 
      :user-info="userInfo"
      :active-menu="activeMenu"
      @switch-system="switchSystem"
      @toggle-fullscreen="toggleFullscreen"
      @refresh-page="refreshPage"
      @prev-tab="prevTab"
      @next-tab="nextTab"
      @user-command="handleCommand"
    />
    <!-- 中间内容区域 -->
    <AppTabs
      :active-tab="activeTab"
      :opened-tabs="openedTabs"
      @change-active="val => (activeTab = val)"
      @tab-click="handleTabClick"
      @tab-remove="handleTabRemove"
      @refresh="refreshPage"
      @prev-tab="prevTab"
      @next-tab="nextTab"
    />

    <!-- 底部内容区域 -->
    <div class="bottom-container flex flex-1 bg-white overflow-hidden box-sizing">
      <!-- 左侧菜单区域 -->
      <AppSidebar
        :first-level-menus="firstLevelMenus"
        :second-level-menus="secondLevelMenus"
        :active-menu="activeMenu"
        :active-first-menu="activeFirstMenu"
        :active-sub-menu="activeSubMenu"
        :is-collapsed="sidebarCollapsed"
        @menu-select="handleMenuSelect"
        @submenu-select="handleSubMenuSelect"
        @toggle-sidebar="handleToggleSidebar"
      />
      <!-- 右侧内容区域 -->
      <div class="main-content flex-1 bg-f5f5f5 pl-8 box-sizing min-w-0 min-h-0">
        <!-- 路由视图 -->
        <router-view :key="refreshKey" />
      </div>
    </div>
    
    <!-- 修改密码弹窗 -->
    <ModifyPassword :visible.sync="modifyPasswordVisible" />
  </div>
</template>

<script>
import { menuApi } from '@/api'
import { loginOut } from '@/api/login'
import { sk } from '@/config'
import { clearAuthentication } from '@/utils/request'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppTabs from '@/components/layout/AppTabs.vue'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import ModifyPassword from '@/components/layout/ModifyPassword.vue'

export default {
  name: 'Layout',
  components: {
    AppHeader,
    AppTabs,
    AppSidebar,
    ModifyPassword,
  },
  data() {
    return {
      userInfo: {
        redisUserName: '系统管理员'
      },
      firstLevelMenus: [],
      secondLevelMenus: [],
      activeFirstMenu: '',
      activeSubMenu: '',
      activeMenu: '',
      activeTab: '',
      openedTabs: [],
      breadcrumbList: [],
      currentPageTitle: '见证管理',
      showSystemSwitcher: false,
      // 内容区域刷新控制
      refreshKey: 0,
      // 侧边栏折叠状态
      sidebarCollapsed: false,
      // 修改密码弹窗显示状态
      modifyPasswordVisible: false,
      // 防止路由变化时重置标签页的标志
      isNavigating: false,
      // 菜单点击防抖定时器
      openTabTimer: null,
    }
  },
  created() {
    this.initUserInfo()
    // 初始进入显示骨架屏，稍后发起菜单请求
    this.initMenus()
    this.initTabs()
    
    // 监听添加tab的事件
    this.$bus.$on('add-tab', this.handleAddTab)
    
    // 监听菜单变化事件
    this.$bus.$on('menuChanged', (menuData) => {
      this.handleMenuChanged(menuData)
    })
    
    // 监听代办任务点击事件
    this.$bus.$on('pending-task-click', this.handlePendingTaskClick)
  },
  beforeDestroy() {
    // 清理事件监听器
    this.$bus.$off('add-tab', this.handleAddTab)
    this.$bus.$off('menuChanged')
    this.$bus.$off('pending-task-click', this.handlePendingTaskClick)
    
    // 清理防抖定时器（虽然 Layout 组件通常不会销毁，但为了安全还是清理）
    if (this.openTabTimer) {
      clearTimeout(this.openTabTimer)
      this.openTabTimer = null
    }
  },
  methods: {
    // 初始化用户信息
    initUserInfo() {
      const userInfo = localStorage.getItem(sk('userInfo'))
      if (userInfo) {
        this.userInfo = JSON.parse(userInfo)
      }
    },
    
    // 初始化菜单（优先使用缓存）
    initMenus() {
      // 先尝试从缓存读取菜单
      let menus = []
      const cached = localStorage.getItem(sk('menus'))
      if (cached) {
        try {
          menus = JSON.parse(cached)
        } catch (e) {
          localStorage.removeItem(sk('menus'))
        }
      }
      
      if (menus && menus.length > 0) {
        this.firstLevelMenus = menus
        this.setupMenus()
        return
      }
      
      menuApi.getMenuData().then(res => {
        if (res.code === 200) {
          this.firstLevelMenus = res.data || []
          if (this.firstLevelMenus.length > 0) {
            localStorage.setItem(sk('menus'), JSON.stringify(this.firstLevelMenus))
          }
        } else {
          this.firstLevelMenus = []
        }
        this.setupMenus()
      }).catch(e => {
        this.firstLevelMenus = []
        this.setupMenus()
      })
    },
    
    // 设置菜单（仅初始化本地菜单状态，不再在此处动态注入路由，避免与路由守卫重复）
    setupMenus() {
      if (this.firstLevelMenus.length > 0) {
        
        // 默认选中第一个一级菜单
        this.activeFirstMenu = this.firstLevelMenus[0].id
        this.secondLevelMenus = this.firstLevelMenus[0].children || []
        
        // 如果有二级菜单，自动选中第一个并打开
        if (this.secondLevelMenus.length > 0) {
          this.activeSubMenu = this.secondLevelMenus[0].id
          this.activeMenu = this.secondLevelMenus[0].id
          // 自动打开第一个二级菜单
          this.openTab(this.secondLevelMenus[0])
          this.updateBreadcrumb(this.secondLevelMenus[0])
        } else {
          this.activeSubMenu = ''
          this.activeMenu = ''
        }
      }
    },
    // 初始化标签页
    initTabs() {
      // 初始化时标签页和面包屑会在initMenus中自动设置
      // 如果还没有打开任何tab，则显示提示信息
      if (this.openedTabs.length === 0) {
        this.breadcrumbList = []
        this.currentPageTitle = '请选择菜单'
      }
    },
    
    // 处理一级菜单选择
    handleMenuSelect(menuId, fromPopup = false) {
      // 如果点击的是当前已选中的一级菜单，不做任何操作
      if (this.activeFirstMenu === menuId) {
        return
      }
      
      this.activeFirstMenu = menuId
      const selectedMenu = this.firstLevelMenus.find(menu => menu.id === menuId)
      if (selectedMenu && selectedMenu.children) {
        // 显示对应的二级菜单
        this.secondLevelMenus = selectedMenu.children
        
        // 如果是弹出菜单的点击，不清空选中状态，让后续的二级菜单选择来处理
        if (fromPopup) {
          // 弹出菜单点击时，不清空选中状态
          return
        }
        
        // 检查当前激活的tab是否在该一级菜单下
        const currentTabInThisMenu = this.secondLevelMenus.find(menu => menu.id === this.activeTab)
        if (currentTabInThisMenu) {
          // 如果当前激活的tab在该一级菜单下，选中对应的二级菜单
          this.activeSubMenu = this.activeTab
          this.activeMenu = this.activeTab
        } else {
          // 如果当前激活的tab不在该一级菜单下，清空选中状态
          this.activeSubMenu = ''
          this.activeMenu = ''
        }
      } else {
        this.secondLevelMenus = []
        this.activeSubMenu = ''
        this.activeMenu = ''
      }
    },
    
    // 处理二级菜单选择
    async handleSubMenuSelect(menuId) {
      console.log('[Layout] handleSubMenuSelect 点击二级菜单:', menuId, '当前路由:', this.$route && this.$route.path)
      this.activeSubMenu = menuId
      this.activeMenu = menuId
      
      const selectedMenu = this.secondLevelMenus.find(menu => menu.id === menuId)
      if (selectedMenu) {
        try {
          // 点击二级菜单时，在中间显示tab并跳转路由
          await this.openTab(selectedMenu)
          this.updateBreadcrumb(selectedMenu)
          
          // 触发全局菜单切换事件，通知帮助对话框更新
          this.$bus.$emit('menuChanged', {
            menuId: selectedMenu.id,
            menuName: selectedMenu.name,
            menuPath: selectedMenu.path,
            menu: selectedMenu
          })
        } catch (error) {
          console.error('[Layout] handleSubMenuSelect 中 openTab 异常:', error)
          // 即使路由跳转失败，也更新UI状态，避免界面卡住
          this.updateBreadcrumb(selectedMenu)
        }
      } else {
        console.warn('[Layout] handleSubMenuSelect 未找到对应二级菜单, menuId =', menuId)
      }
    },
    
    // 打开标签页（带防抖处理，避免多次触发）
    openTab(menu) {
      // 清除之前的定时器，实现防抖：500ms内只执行一次
      if (this.openTabTimer) {
        clearTimeout(this.openTabTimer)
      }
      
      // 立即更新 UI 状态，让用户感觉立即响应
      const existingTab = this.openedTabs.find(tab => tab.name === menu.id)
      if (!existingTab) {
        const newTab = {
          name: menu.id,
          title: menu.name,
          path: menu.path
        }
        this.openedTabs.push(newTab)
      }
      this.activeTab = menu.id
      // 同步左侧菜单高亮
      this.syncMenuByTab(menu.id)
      this.updateBreadcrumb(menu) // 立即更新面包屑
      
      // 防抖：延迟执行路由跳转，500ms内多次点击只执行最后一次
      this.openTabTimer = setTimeout(() => {
        const currentPath = this.$route && this.$route.path
        // 检查当前路由是否与目标路由相同，避免重复导航
        if (currentPath !== menu.path) {
          console.log('[Layout] 路由跳转开始, from:', currentPath, 'to:', menu.path)
          
          // 不等待 router.push() 完成，让路由在后台跳转
          // 这样路由会立即跳转，组件在后台加载
          // 注意：router.push 已经在 router/index.js 中重写，会自动忽略重复导航错误
          this.$router.push(menu.path).catch(err => {
            // 其他错误正常处理（重复导航错误已被 router.push 重写忽略）
            if (err.name !== 'NavigationDuplicated') {
              console.warn('[Layout] 路由跳转异常（后台处理）:', err && err.message)
            }
          })
          
          // 监听路由变化，确保状态同步
          this.$nextTick(() => {
            const unwatch = this.$watch('$route.path', (newPath) => {
              if (newPath === menu.path) {
                // 路由已跳转，确保状态同步
                this.syncMenuByTab(menu.id)
                this.updateBreadcrumb(menu)
                unwatch()
              }
            })
            // 10秒后自动清理监听器
            setTimeout(() => {
              unwatch()
            }, 10000)
          })
        } else {
          console.log('[Layout] 当前路由已是目标路由, 跳过导航:', menu.path)
        }
        
        this.openTabTimer = null
      }, 100) // 100ms 防抖延迟，既保证响应速度，又避免重复触发
    },
    
    // 处理添加tab的事件（简化版本）
    handleAddTab(tabData) {
      try {
        // 设置导航标志，防止路由监听器干扰
        this.isNavigating = true;
        
        // 根据path查找实际的菜单，获取真实的菜单ID
        let actualMenuId = tabData.name
        if (tabData.path) {
          const targetMenu = this.findMenuByPath(tabData.path)
          if (targetMenu) {
            actualMenuId = targetMenu.id
          }
        }
        
        // 检查是否已经存在相同的tab（按name、path或实际菜单ID匹配）
        const existingTab = this.openedTabs.find(tab => 
          tab.name === tabData.name || 
          tab.path === tabData.path ||
          tab.name === actualMenuId
        );
        if (existingTab) {
          // 如果已存在，直接激活该tab
          this.activeTab = existingTab.name;
          this.syncMenuByTab(existingTab.name);
          // 延迟重置导航标志
          this.$nextTick(() => {
            this.isNavigating = false;
          });
          return;
        }
        
        // 创建新的tab，使用实际的菜单ID作为name
        const newTab = {
          name: actualMenuId, // 使用真实的菜单ID
          title: tabData.title,
          path: tabData.path
        };
        
        // 添加到tabs数组
        this.openedTabs.push(newTab);
        
        // 激活新创建的tab
        this.activeTab = actualMenuId;
        
        // 同步左侧菜单
        this.syncMenuByTab(actualMenuId);
        
        // 使用双重确保机制，确保标签页被正确激活
        this.$nextTick(() => {
          this.activeTab = actualMenuId;
          // 再次确保同步
          this.syncMenuByTab(actualMenuId);
          // 延迟重置导航标志
          setTimeout(() => {
            this.isNavigating = false;
          }, 100);
        });
      } catch (error) {
        console.error('处理添加tab事件时出错:', error);
        this.isNavigating = false;
      }
    },
    
    // 根据标签ID同步左侧菜单选中与二级列表
    syncMenuByTab(tabId) {
      if (!tabId) return
      let parentMenu = null
      let targetChild = null
      for (const fm of this.firstLevelMenus) {
        const child = (fm.children || []).find(c => c.id === tabId)
        if (child) {
          parentMenu = fm
          targetChild = child
          break
        }
      }
      if (parentMenu && targetChild) {
        this.activeFirstMenu = parentMenu.id
        this.secondLevelMenus = parentMenu.children || []
        this.activeSubMenu = tabId
        this.activeMenu = tabId
      }
    },
    
    // 更新面包屑
    updateBreadcrumb(menu) {
      const firstMenu = this.firstLevelMenus.find(fm => fm.id === this.activeFirstMenu)
      this.breadcrumbList = [
        { name: firstMenu.name, path: firstMenu.path },
        { name: menu.name, path: menu.path }
      ]
      this.currentPageTitle = menu.name
    },
    
    // 处理标签页点击（简化版本）
    handleTabClick(tab) {
      const targetTab = this.openedTabs.find(t => t.name === tab.name)
      if (targetTab) {
        // 同步左侧菜单
        this.syncMenuByTab(targetTab.name)
        
        // 进行路由跳转
        if (this.$route.path !== targetTab.path) {
          this.$router.push(targetTab.path).catch(err => {
            // 忽略导航重复错误
            if (err.name !== 'NavigationDuplicated') {
              console.error(err)
            }
          })
        }
      }
    },
    
    // 处理标签页关闭
    handleTabRemove(tabName) {
      // 保留至少一个标签
      if (this.openedTabs.length <= 1) {
        this.$message.warning('至少保留一个标签页')
        return
      }
      const tabIndex = this.openedTabs.findIndex(tab => tab.name === tabName)
      if (tabIndex > -1) {
        this.openedTabs.splice(tabIndex, 1)
        if (this.activeTab === tabName) {
          if (this.openedTabs.length > 0) {
            const lastTab = this.openedTabs[this.openedTabs.length - 1]
            this.activeTab = lastTab.name
            this.syncMenuByTab(lastTab.name)
            this.$router.push(lastTab.path)
          }
        }
      }
    },
    
    // 切换系统
    switchSystem() {
      this.showSystemSwitcher = true
    },
    onSystemSelect(key) {
      // 记录选择并提示，按需跳转首页或刷新
      localStorage.setItem(sk('active_system'), key)
      this.$message.success('已切换到：' + (key === 'witness' ? '监造执行' : '经营管理'))
      // 示例：切换后返回首页
      this.$router.replace('/')
    },
    
    // 全屏切换
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen()
      } else {
        document.exitFullscreen()
      }
    },
    
    // 处理用户下拉菜单
    handleCommand(command) {
      switch (command) {
        case 'profile':
          this.$message.info('个人信息功能待开发')
          break
        case 'changePassword':
          this.modifyPasswordVisible = true
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },
    
    // 退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 调用退出登录接口
        loginOut().then(() => {
          // 接口调用成功后清理本地数据
          this.clearLocalData()
          this.$message.success('已退出登录')
        }).catch(error => {
          // 即使接口调用失败，也要清理本地数据
          this.clearLocalData()
          this.$message.success('已退出登录')
        })
      }).catch(() => {
        // 用户取消退出
      })
    },
    
    // 清理本地数据
    clearLocalData() {
      clearAuthentication()
      // 清空页面状态
      this.openedTabs = []
      this.activeTab = ''
      this.firstLevelMenus = []
      this.secondLevelMenus = []
      this.activeFirstMenu = ''
      this.activeSubMenu = ''
      this.activeMenu = ''
      // 跳转登录
      this.$router.replace('/login').catch(() => {})
    },
    
    // 刷新页面
    refreshPage() {
      // 通过改变 key 值来强制重新渲染内容区域
      this.refreshKey = Date.now()
    },
    
    // 清除菜单缓存并重新加载
    clearMenuCache() {
      localStorage.removeItem(sk('menus'))
      this.initMenus()
    },
    
    // 上一个标签页
    prevTab() {
      const currentIndex = this.openedTabs.findIndex(tab => tab.name === this.activeTab)
      if (this.openedTabs.length === 0) return
      if (currentIndex > 0) {
        const prevTab = this.openedTabs[currentIndex - 1]
        this.activeTab = prevTab.name
        this.syncMenuByTab(prevTab.name)
        this.$router.push(prevTab.path)
      } else {
        const lastTab = this.openedTabs[this.openedTabs.length - 1]
        this.activeTab = lastTab.name
        this.syncMenuByTab(lastTab.name)
        this.$router.push(lastTab.path)
      }
    },
    
    // 下一个标签页
    nextTab() {
      const currentIndex = this.openedTabs.findIndex(tab => tab.name === this.activeTab)
      if (this.openedTabs.length === 0) return
      if (currentIndex < this.openedTabs.length - 1) {
        const nextTab = this.openedTabs[currentIndex + 1]
        this.activeTab = nextTab.name
        this.syncMenuByTab(nextTab.name)
        this.$router.push(nextTab.path)
      } else {
        const firstTab = this.openedTabs[0]
        this.activeTab = firstTab.name
        this.syncMenuByTab(firstTab.name)
        this.$router.push(firstTab.path)
      }
    },
    
    // 处理侧边栏折叠切换
    handleToggleSidebar(collapsed) {
      this.sidebarCollapsed = collapsed
    },

    // 根据路由同步菜单状态
    syncMenuByRoute(path) {
      if (!path) return
      let targetMenu = null
      for (const fm of this.firstLevelMenus) {
        const child = (fm.children || []).find(c => c.path === path)
        if (child) {
          targetMenu = child
          break
        }
      }
      if (targetMenu) {
        this.syncMenuByTab(targetMenu.id)
        this.updateBreadcrumb(targetMenu)
        this.activeTab = targetMenu.id
      } else {
        // 如果路由对应的菜单不存在，则尝试从已打开的tab中查找
        const existingTab = this.openedTabs.find(tab => tab.path === path)
        if (existingTab) {
          this.syncMenuByTab(existingTab.name)
          this.updateBreadcrumb(existingTab)
          this.activeTab = existingTab.name
        } else {
          // 如果是见证任务页面，特殊处理
          if (path === '/witness/task') {
            // 查找见证管理菜单
            const witnessMenu = this.firstLevelMenus.find(fm => fm.name === '见证管理' || fm.id === 'witness')
            if (witnessMenu && witnessMenu.children) {
              // 查找见证任务子菜单
              const taskMenu = witnessMenu.children.find(child => child.name === '见证任务' || child.id === 'task')
              if (taskMenu) {
                // 确保标签页存在
                const existingTab = this.openedTabs.find(tab => tab.name === 'task')
                if (!existingTab) {
                  this.openedTabs.push({
                    name: 'task',
                    title: '见证任务',
                    path: '/witness/task'
                  })
                }
                
                this.syncMenuByTab(taskMenu.id)
                this.updateBreadcrumb(taskMenu)
                this.activeTab = 'task'
                return
              }
            }
          }
          
          // 如果当前路由不在任何菜单或已打开的tab中，则重置菜单状态
          this.activeFirstMenu = ''
          this.activeSubMenu = ''
          this.activeMenu = ''
          this.activeTab = ''
          this.openedTabs = []
          this.breadcrumbList = []
          this.currentPageTitle = '请选择菜单'
          // 如果是根路径，不进行路由跳转
          if (path !== '/') {
            this.$router.push('/')
          }
        }
      }
    },
    
    // 处理菜单变化事件
    handleMenuChanged(menuData) {
      if (!menuData) return
      
      // 如果提供了 menuPath，优先使用路径匹配（更准确，避免名称匹配错误）
      if (menuData.menuPath) {
        const targetMenu = this.findMenuByPath(menuData.menuPath)
        if (targetMenu) {
          // 找到对应的菜单，同步菜单状态
          this.syncMenuByTab(targetMenu.id)
          return
        }
      }
      
      // 备用方案：使用菜单名称匹配
      if (menuData.menuName) {
        const firstLevelMenu = this.firstLevelMenus.find(menu => {
          return menu.children && menu.children.some(child => {
            return child.name === menuData.menuName
          })
        })
        
        if (firstLevelMenu) {
          this.activeFirstMenu = firstLevelMenu.id
          this.secondLevelMenus = firstLevelMenu.children
          
          // 找到匹配的子菜单，使用其真实ID
          const matchedChild = firstLevelMenu.children.find(child => child.name === menuData.menuName)
          if (matchedChild) {
            this.activeMenu = matchedChild.id
            this.activeSubMenu = matchedChild.id
          }
        }
      }
    },
    
    // 处理代办任务点击事件
    handlePendingTaskClick(taskData) {
      if (!taskData || !taskData.goUrl) return
      
      // 根据goUrl查找对应的菜单项
      const targetMenu = this.findMenuByPath(taskData.goUrl)
      
      if (targetMenu) {
        // 打开对应的tab
        this.openTab(targetMenu)
        this.updateBreadcrumb(targetMenu)
        
        // 触发全局菜单切换事件
        this.$bus.$emit('menuChanged', {
          menuId: targetMenu.id,
          menuName: targetMenu.name,
          menuPath: targetMenu.path,
          menu: targetMenu
        })
      } else {
      }
    },
    
    // 根据路径查找菜单项
    findMenuByPath(path) {
      if (!path) return null
      
      // 遍历所有一级菜单的子菜单
      for (const firstMenu of this.firstLevelMenus) {
        if (!firstMenu.children) continue
        
        for (const childMenu of firstMenu.children) {
          if (!childMenu.path) continue
          
          // 只使用精确匹配，避免模糊匹配导致的错误匹配
          // 1. 精确匹配（原始大小写）- 最高优先级
          if (childMenu.path === path) {
            return childMenu
          }
          
          // 2. 精确匹配（忽略大小写）- 次高优先级
          if (childMenu.path.toLowerCase() === path.toLowerCase()) {
            return childMenu
          }
        }
      }
      
      return null
    }
    
  },
  watch: {
    // 监听路由变化，同步菜单状态
    '$route'(to, from) {
      // 如果正在导航过程中，不执行同步，避免覆盖标签页状态
      if (this.isNavigating) {
        return;
      }
      this.syncMenuByRoute(to.path)
    }
  }
}
</script>

<style scoped>

.app-wrapper {
  overflow: hidden; /* 防止整体滚动条 */
}

.app-wrapper * {
  box-sizing: border-box;
}


/* 避免内容溢出，允许右侧主体内滚动 */
.bottom-container { 
  min-height: 0; 
  overflow: hidden; /* 防止底部容器滚动条 */
}
.main-content { 
  min-width: 0; 
  min-height: 0; 
  overflow: hidden; /* 防止主内容区域滚动条 */
  position: relative;
}

</style>
